import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import styled from 'styled-components';
import { FileText, Brain, Database, User, MessageCircle, Smile, DollarSign, Gift, ShieldAlert, BookOpen } from 'lucide-react';
import type { ScriptNodeData } from './types';
import { SCRIPT_COLORS } from './types';

// Mapa de ícones por tipo de script
const SCRIPT_ICONS: Record<string, React.ElementType> = {
  conteudo: FileText,
  mentalidade: Brain,
  fatos_produto: Database,
  persona: User,
  girias: MessageCircle,
  abreviacoes: MessageCircle,
  emojis: Smile,
  preco: DollarSign,
  beneficios: Gift,
  objecoes: ShieldAlert,
  storytelling: BookOpen,
};

// Mapa de nomes formatados para exibição
const SCRIPT_LABELS: Record<string, string> = {
  conteudo: 'Conteúdo',
  mentalidade: 'Mentalidade',
  fatos_produto: 'Fatos do Produto',
  persona: 'Persona',
  girias: 'Gírias',
  abreviacoes: 'Abreviações',
  emojis: 'Emojis',
  preco: 'Preço',
  beneficios: 'Benefícios',
  objecoes: 'Objeções',
  storytelling: 'Storytelling',
};

// Mapa de tipos agrupados (para badge de tipo)
const SCRIPT_TYPE_LABELS: Record<string, string> = {
  conteudo: 'Conteúdo',
  mentalidade: 'Mentalidade',
  fatos_produto: 'Produto',
  persona: 'Persona',
  // Escrita (unificado)
  girias: 'Escrita',
  abreviacoes: 'Escrita',
  emojis: 'Escrita',
  preco: 'Conteúdo',
  beneficios: 'Conteúdo',
  objecoes: 'Conteúdo',
  storytelling: 'Conteúdo',
};

const formatTypeLabel = (type: string): string => {
  return SCRIPT_TYPE_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Função para formatar nome do script
const formatScriptName = (name: string): string => {
  // Primeiro verifica se existe no mapa
  const lowerName = name.toLowerCase().replace(/[_-]/g, '_');
  if (SCRIPT_LABELS[lowerName]) {
    return SCRIPT_LABELS[lowerName];
  }
  // Fallback: capitaliza e substitui underscores por espaços
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

const NodeContainer = styled.div<{ $selected: boolean; $type: string }>`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 2px solid ${({ $selected, theme }) => $selected ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  min-width: 220px;
  box-shadow: ${({ $selected, theme }) =>
    $selected ? theme.shadows.neon : theme.shadows.md};
  transition: all ${({ theme }) => theme.animations.duration.normal} ${({ theme }) => theme.animations.easing.easeInOut};
  overflow: hidden;
  cursor: pointer;
  user-select: none;
  pointer-events: all;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    transform: translateY(-2px);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const NodeHeader = styled.div<{ $type: string }>`
  background: ${props => SCRIPT_COLORS[props.$type as keyof typeof SCRIPT_COLORS]};
  padding: ${({ theme }) => theme.spacing[3]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  color: white;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  min-height: 44px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

const NodeTitle = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NodeBody = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[2]} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const MetaLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const MetaValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const TypeBadge = styled.span<{ $type: string }>`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  background: ${props => SCRIPT_COLORS[props.$type as keyof typeof SCRIPT_COLORS]}20;
  color: ${props => {
    const colorMap: Record<string, string> = {
      conteudo: '#6b7280',
      mentalidade: '#ef4444',
      fatos_produto: '#f59e0b',
      persona: '#8b5cf6',
      // Escrita (unificado - verde)
      girias: '#10b981',
      abreviacoes: '#10b981',
      emojis: '#10b981',
    };
    return colorMap[props.$type] || props.theme.colors.textSecondary;
  }};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const CustomHandle = styled(Handle)`
  width: 10px;
  height: 10px;
  background: ${({ theme }) => theme.colors.primary};
  border: 2px solid ${({ theme }) => theme.colors.backgroundSecondary};
  transition: all 0.2s;

  &:hover {
    width: 14px;
    height: 14px;
    background: ${({ theme }) => theme.colors.primaryHover};
  }
`;

/**
 * Nó customizado para scripts (React Flow)
 * Design inspirado em n8n com cores vibrantes e metadados úteis
 */
export const ScriptNode: React.FC<NodeProps<ScriptNodeData>> = ({ data, selected }) => {
  // Fallback para FileText se o tipo não existir no mapa
  const Icon = SCRIPT_ICONS[data.scriptType] || FileText;

  return (
    <>
      <CustomHandle type="target" position={Position.Top} />

      <NodeContainer $selected={selected || false} $type={data.scriptType}>
        <NodeHeader $type={data.scriptType}>
          <IconWrapper>
            <Icon size={20} />
          </IconWrapper>
          <NodeTitle>{formatScriptName(data.scriptName)}</NodeTitle>
        </NodeHeader>

        <NodeBody>
          <MetaRow>
            <MetaLabel>Tipo</MetaLabel>
            <TypeBadge $type={data.scriptType}>{formatTypeLabel(data.scriptType)}</TypeBadge>
          </MetaRow>
          <MetaRow>
            <MetaLabel>Linhas</MetaLabel>
            <MetaValue>{data.lineCount || 0}</MetaValue>
          </MetaRow>
          {data.intention && (
            <MetaRow>
              <MetaLabel>Intenção</MetaLabel>
              <MetaValue>{data.intention}</MetaValue>
            </MetaRow>
          )}
        </NodeBody>
      </NodeContainer>

      <CustomHandle type="source" position={Position.Bottom} />
    </>
  );
};
